import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Tooltip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Stack,
    Avatar
} from "@mui/material";
import { format, differenceInDays } from "date-fns";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';

import { useEmpleadoDetalle } from "../../hooks/useEmployees";
import { useDocuments } from "../../hooks/useDocuments";
import DocumentsForm from "../administracion/DocumentsForm";

import { useAuthContext } from "../../context/AuthProvider";

const estadoColor = {
    VIGENTE: "success",
    "POR VENCER": "warning",
    VENCIDO: "error",
    "NO REGISTRADO": "default",
};

const getEstadoDocumento = (doc) => {
    if (!doc) return "NO REGISTRADO";
    return doc.docest || "NO REGISTRADO";
};

const EmployeeDetailDashboard = () => {
    const { user } = useAuthContext();
    const { id } = useParams();
    const [selectedProject, setSelectedProject] = useState(null);
    const [documentosRequeridos, setDocumentosRequeridos] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [openPreview, setOpenPreview] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [editDocument, setEditDocument] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [formDefaultValues, setFormDefaultValues] = useState({});

    const { createMutation, updateMutation } = useDocuments();

    const [formConfig, setFormConfig] = useState({
        forcedAssociationType: "vehiculo",
        lockAssociationType: false,
        lockDocumentType: false,
        lockVehicle: false,
        lockEmployee: false,
    });

    const {
        empleado,
        proyectos,
        documentosEmpleado,
        isLoading,
        error,
        fetchProjectDocuments,
        refetchDocumentosEmpleado,
    } = useEmpleadoDetalle(id);

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleSubmitDocument = (data) => {
        if (!user) {
            showAlert("Debe iniciar sesión para realizar esta acción", "error");
            return;
        }

        const isEditing = data.get("docid");

        if (isEditing) {
            data.append("updbyid", user.EmpID);
            updateMutation.mutate(
                { id: data.get("docid"), data },
                {
                    onSuccess: (result) => {
                        if (result?.error) {
                            showAlert(result.error, "error");
                        } else {
                            showAlert("Documento actualizado con éxito");
                            setOpenForm(false);
                            setEditDocument(null);
                            refetchDocumentosEmpleado();
                        }
                    },
                    onError: (error) => {
                        const errorMessage = error.response?.data?.error || "Error al actualizar el documento";
                        showAlert(errorMessage, "error");
                    },
                }
            );
        } else {
            data.append("crebyid", user.EmpID);
            createMutation.mutate(data, {
                onSuccess: (result) => {
                    if (result?.error) {
                        showAlert(result.error, "error");
                        setOpenForm(false);
                    } else {
                        showAlert("Documento creado con éxito");
                        setOpenForm(false);
                        refetchDocumentosEmpleado();
                    }
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.error || "Error al crear el documento";
                    showAlert(errorMessage, "error");
                },
            });
        }
    };


    const handleOpenPreview = (ruta) => {
        const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${ruta}`;
        setPreviewUrl(fullUrl);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewUrl(null);
    };

    const handleShowAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenProject = async (proyecto) => {
        const docs = await fetchProjectDocuments(proyecto.proid);
        const conductorDocs = docs.filter((doc) => doc.reqent === "CONDUCTOR");

        setSelectedProject(proyecto);
        setDocumentosRequeridos(conductorDocs);
    };

    const handleOpenForm = (data = {}, options = {}) => {
        const isEditing = !!data.docid; // Si tiene docid, es edición
        const enrichedData = {
            ...data,
            empid: parseInt(id, 10),
            associationType: "empleado",
        };

        if (!isEditing) {
            // Es creación: aseguramos que campos de edición no estén presentes
            delete enrichedData.docid;
            delete enrichedData.docrut;
            delete enrichedData.docest;
            delete enrichedData.docfecemi;
            delete enrichedData.docfecven;
        }

        setFormDefaultValues(enrichedData);

        setFormConfig({
            forcedAssociationType: "empleado",
            lockAssociationType: true,
            lockDocumentType: !!options.lockDocumentType,
            lockVehicle: true,
            lockEmployee: true,
        });

        setOpenForm(true);
    };

    if (isLoading) return <Typography>Cargando datos...</Typography>;
    if (error) return <Typography color="error">Error al cargar información</Typography>;

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Detalle del Empleado</Typography>

            <Stack spacing={2} alignItems="center" mb={3}>
                {/* Avatar and Name */}
                <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80 }}>
                    <PersonIcon sx={{ fontSize: 50 }} />
                </Avatar>
                <Typography variant="h5" component="h2" align="center" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {empleado.pernom} {empleado.perape}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" align="center">
                    {empleado.rolnom} {empleado.empcargo ? `(ID: ${empleado.empid})` : ''}
                </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
                {/* Contact Information */}
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Usuario:</strong> {empleado.nomusu}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>DNI:</strong> {empleado.perdni}
                    </Typography>
                </Grid>

                {/* Personal / System Information */}
                <Grid item xs={12} sm={6}>
                    {empleado.pertel && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Teléfono:</strong> {empleado.pertel}
                        </Typography>
                    )}
                    {empleado.perema && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong> {empleado.perema}
                        </Typography>
                    )}
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>Proyectos Asociados</Typography>
            <Grid container spacing={2} mb={3}>
                {proyectos.map((proyecto) => {
                    const isSelected = selectedProject?.proid === proyecto.proid;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={proyecto.proid}>
                            <Paper
                                onClick={() => handleOpenProject(proyecto)}
                                elevation={isSelected ? 6 : 1}
                                sx={{
                                    p: 2,
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                    backgroundColor: isSelected ? "#e3f2fd" : "#fff",
                                    transition: "all 0.2s ease-in-out",
                                    '&:hover': {
                                        boxShadow: 4,
                                        backgroundColor: "#f5f5f5",
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <AssignmentIcon color={isSelected ? "primary" : "action"} />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {proyecto.pronom}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {selectedProject && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Documentos Requeridos – {selectedProject.pronom}
                    </Typography>

                    <TableContainer component={Paper} elevation={3}>
                        <Table>
                            <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
                                <TableRow>
                                    <TableCell><strong>Tipo de Documento</strong></TableCell>
                                    <TableCell><strong>Fecha Emisión</strong></TableCell>
                                    <TableCell><strong>Fecha Vencimiento</strong></TableCell>
                                    <TableCell><strong>Vence En</strong></TableCell>
                                    <TableCell><strong>Estado</strong></TableCell>
                                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentosRequeridos.map((docReq) => {
                                    const documentoEmpleado = documentosEmpleado.find(doc => doc.doctipid === docReq.doctipid);
                                    const estado = getEstadoDocumento(documentoEmpleado);
                                    const hoy = new Date();
                                    const diasRestantes = documentoEmpleado?.docfecven
                                        ? Math.ceil((new Date(documentoEmpleado.docfecven) - hoy) / (1000 * 60 * 60 * 24))
                                        : null;

                                    return (
                                        <TableRow key={docReq.prodocreqid}>
                                            <TableCell>{docReq.doctip}</TableCell>
                                            <TableCell>
                                                {documentoEmpleado?.docfecemi
                                                    ? format(new Date(documentoEmpleado.docfecemi), "dd/MM/yyyy")
                                                    : "--"}
                                            </TableCell>
                                            <TableCell>
                                                {documentoEmpleado?.docfecven
                                                    ? format(new Date(documentoEmpleado.docfecven), "dd/MM/yyyy")
                                                    : "--"}
                                            </TableCell>
                                            <TableCell>
                                                {documentoEmpleado?.docest === "POR VENCER" && documentoEmpleado?.docfecven && (() => {
                                                    const today = new Date();
                                                    const vencimiento = new Date(documentoEmpleado?.docfecven);
                                                    const diffDays = Math.ceil((vencimiento - today) / (1000 * 60 * 60 * 24));

                                                    let color = '';
                                                    let bgColor = '';
                                                    let Icon = AccessTimeIcon;

                                                    if (diffDays < 20) {
                                                        color = 'red';
                                                        bgColor = '#ffe5e5';
                                                        Icon = ErrorOutlineIcon;
                                                    } else if (diffDays < 40) {
                                                        color = 'orange';
                                                        bgColor = '#fff4e5';
                                                        Icon = WarningIcon;
                                                    } else {
                                                        color = 'green';
                                                        bgColor = '#e5fbe5';
                                                        Icon = AccessTimeIcon;
                                                    }

                                                    return (
                                                        <Box
                                                            display="inline-flex"
                                                            alignItems="center"
                                                            gap={0.5}
                                                            px={1}
                                                            py={0.5}
                                                            borderRadius={2}
                                                            sx={{
                                                                backgroundColor: bgColor,
                                                                color: color,
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            <Icon fontSize="small" />
                                                            <Typography variant="body2">{diffDays} días</Typography>
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={estado}
                                                    color={estadoColor[estado]}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" justifyContent="center" gap={1}>
                                                    {documentoEmpleado?.docrut && (
                                                        <Tooltip title="Ver Documento">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: "#607d8b" }}
                                                                onClick={() => handleOpenPreview(documentoEmpleado.docrut)}>
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {documentoEmpleado ? (
                                                        <Tooltip title="Editar Documento">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenForm(documentoEmpleado, { lockDocumentType: true })}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Registrar Documento">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenForm(
                                                                    {
                                                                        empid: parseInt(id),
                                                                        doctipid: docReq.doctipid,
                                                                        associationType: "empleado",
                                                                    },
                                                                    {
                                                                        lockDocumentType: true
                                                                    }
                                                                )}
                                                            >
                                                                <AddIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}


            {/* Modal para ver documento */}
            <Dialog open={openPreview} onClose={handleClosePreview} fullWidth maxWidth="xl">
                <DialogTitle>Vista Previa del Documento</DialogTitle>
                <DialogContent dividers>
                    {previewUrl?.endsWith(".pdf") ? (
                        <iframe src={previewUrl} width="100%" height="500px" style={{ border: "none" }} />
                    ) : (
                        <img src={previewUrl} alt="Documento" style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview} color="secondary">Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal para crear/editar documento */}
            {openForm && (
                <DocumentsForm
                    open={openForm}
                    handleClose={() => {
                        setOpenForm(false);
                        setEditDocument(null);
                    }}
                    onSubmit={handleSubmitDocument}
                    defaultValues={formDefaultValues || {}}
                    config={formConfig}
                />
            )}

            {/* Alertas */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default EmployeeDetailDashboard;
